import type { IConfigurationEntity, SettingsData } from "../../../interfaces";

export enum EMailSettingKey {
    showWelcomeScreen = "showWelcomeScreen",
    showMetadataWarning = "showMetadataWarning",
    showTreeView = "showTreeView",
    hideUploadScreen = "hideUploadScreen",
    addUrlCategory = "addUrlCategory",
    defaultToQuickSave = "defaultToQuickSave",
    defaultItemSelection = "defaultItemSelection",
    emailFileNameFormat = "emailFileNameFormat",
    emailContentType = "emailContentType",
    favoriteSubscriptionUrl = "favoriteSubscriptionUrl",
    moveToFolder = "moveToFolder",
    folderName = "folderName",
    enableSyncFavorites = "enableSyncFavorites",
    syncFavoritesUrl = "syncFavoritesUrl",
    insertAsLink = "insertAsLink",
    useDocIdRedirect = "useDocIdRedirect",
    applyEmailPropetiesToAttachments = "applyEmailPropertiesToAttachments",
    showSaveAndSendPromptWhileSending = "showSaveAndSendPromptWhileSending",
    showReminderBanner = "showReminderBanner"
}

export const emailItemsSelectionOptions = [
    { key: 0, text: "Email message only" },
    { key: 1, text: "Attachments" },
    { key: 2, text: "Email message and attachments" }];

export const MailSetting: Record<EMailSettingKey, IConfigurationEntity> = {
    [EMailSettingKey.showWelcomeScreen]: {
        key: EMailSettingKey.showWelcomeScreen,
        name: "Show Welcome screen",
        shortDescription: "Show the welcome screen on startup",
        defaultValue: true,
        type: "boolean"
    },
    [EMailSettingKey.showMetadataWarning]: {
        key: EMailSettingKey.showMetadataWarning,
        name: "Show metadata warning message",
        defaultValue: true,
        shortDescription: "Show warning message when mail metadata is not being saved",
        type: "boolean"
    },
    [EMailSettingKey.showTreeView]: {
        key: EMailSettingKey.showTreeView,
        name: "Show tree view",
        defaultValue: true,
        shortDescription: "Show tree view on non-mobile devices",
        type: "boolean"
    },
    [EMailSettingKey.hideUploadScreen]: {
        key: EMailSettingKey.hideUploadScreen,
        name: "Automatically close/restart when uploading",
        defaultValue: false,
        shortDescription: "Automatically close/restart the MacroView Mail task pane when uploading",
        type: "boolean"
    },
    [EMailSettingKey.addUrlCategory]: {
        key: EMailSettingKey.addUrlCategory,
        name: "Add save location category",
        defaultValue: true,
        shortDescription: "Add save location category to saved emails",
        type: "boolean"
    },
    [EMailSettingKey.defaultToQuickSave]: {
        key: EMailSettingKey.defaultToQuickSave,
        name: "Default to quick save",
        defaultValue: false,
        shortDescription: "Default to quick save",
        type: "boolean"
    },
    [EMailSettingKey.applyEmailPropetiesToAttachments]: {
        key: EMailSettingKey.applyEmailPropetiesToAttachments,
        name: "Reuse email properties to apply to attachments",
        defaultValue: false,
        shortDescription: "Enable saving attachments together with email metadata, if the attachment content type includes corresponding metadata fields.",
        type: "boolean"
    },
    [EMailSettingKey.defaultItemSelection]: {
        key: EMailSettingKey.defaultItemSelection,
        name: "Items selected for saving",
        defaultValue: 0,
        shortDescription: "Default email items selected for saving",
        type: "number"
    },
    [EMailSettingKey.emailFileNameFormat]: {
        key: EMailSettingKey.emailFileNameFormat,
        name: "Email file name format",
        defaultValue: "[SenderName]_[SentOnAsGMT]_[Subject_First20]",
        shortDescription: "Preferred email file name format",
        type: "string"
    },
    [EMailSettingKey.emailContentType]: {
        key: EMailSettingKey.emailContentType,
        name: "Email content type",
        defaultValue: "Email",
        shortDescription: "Preferred email content type",
        type: "string"
    },
    [EMailSettingKey.favoriteSubscriptionUrl]: {
        key: EMailSettingKey.favoriteSubscriptionUrl,
        name: "Favorite subscription URL",
        defaultValue: "",
        shortDescription: "URL to a site containing a Macroview Push Favorites Subscription list",
        type: "URL"
    },
    [EMailSettingKey.moveToFolder]: {
        key: EMailSettingKey.moveToFolder,
        name: "Move emails to folder",
        defaultValue: false,
        shortDescription: "Enable moving emails to a folder after saving",
        type: "boolean"
    },
    [EMailSettingKey.folderName]: {
        key: EMailSettingKey.folderName,
        name: "Folder display name",
        defaultValue: "Saved to SharePoint",
        shortDescription: "Outlook folder display name of folder where emails are moved to after saving",
        type: "string"
    },
    [EMailSettingKey.enableSyncFavorites]: {
        key: EMailSettingKey.enableSyncFavorites,
        name: "Sync Favorites",
        defaultValue: true,
        shortDescription: "Enable Sync Favorites",
        type: "boolean"
    },
    [EMailSettingKey.syncFavoritesUrl]: {
        key: EMailSettingKey.syncFavoritesUrl,
        name: "Remote path for Sync Favorites",
        defaultValue: "",
        shortDescription: "URL for Sync Favorites",
        type: "URL"
    },
    [EMailSettingKey.insertAsLink]: {
        key: EMailSettingKey.insertAsLink,
        name: "Insert as links",
        defaultValue: false,
        shortDescription: "Insert attachments as links",
        type: "boolean"
    },
    [EMailSettingKey.useDocIdRedirect]: {
        key: EMailSettingKey.useDocIdRedirect,
        name: "Use DocID redirect links",
        defaultValue: false,
        shortDescription: "Use SharePoint Document ID redirect when inserting links",
        type: "boolean"
    },
    [EMailSettingKey.showSaveAndSendPromptWhileSending]: {
        key: EMailSettingKey.showSaveAndSendPromptWhileSending,
        name: "Show a prompt to save the email while sending it",
        defaultValue: false,
        shortDescription: "Show a prompt to save the email while sending it",
        type: "boolean"
    },
    [EMailSettingKey.showReminderBanner]: {
        key: EMailSettingKey.showReminderBanner,
        name: "Show reminder banner",
        defaultValue: true,
        shortDescription: "Show a reminder banner with 'Save & send' button while composing an email",
        type: "boolean"
    }
};

export const MailDefaultSettings: SettingsData = {
    [EMailSettingKey.emailFileNameFormat]: MailSetting[EMailSettingKey.emailFileNameFormat].defaultValue,
    [EMailSettingKey.emailContentType]: MailSetting[EMailSettingKey.emailContentType].defaultValue
};